import { useState } from 'react';
import { Download, X, ZoomIn } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import ListItem from './ListItem';
import { useNotify, useTranslate } from 'ra-core';
import { downloadFile } from '../../utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
} from '@/components/ui/dialog';

type Props = {
  url: string;
  width?: number;
  height?: number;
  fileName: string;
};

function ImageAssetItem({ url, width, height, fileName }: Props) {
  const notify = useNotify();
  const t = useTranslate();
  const [open, setOpen] = useState(false);

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!(await downloadFile(url, fileName))) {
      notify(t('error.oops'), { type: 'error' });
    }
  };

  return (
    <>
      <ListItem
        primaryText={fileName}
        secondaryText={width && height ? `${width}×${height}` : ''}
        icon={
          <button
            onClick={() => setOpen(true)}
            className="relative group overflow-hidden rounded cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title="View full size"
          >
            <img src={url} alt="" className="block" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <ZoomIn className="size-4 text-white" />
            </div>
          </button>
        }
        secondaryAction={
          <Button variant="ghost" size="icon" onClick={handleDownload}>
            <Download className="size-4 text-muted-foreground" />
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-black/80" />
          <DialogPrimitive.Content
            className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
            onEscapeKeyDown={e => {
              e.stopPropagation();
              setOpen(false);
            }}
            onClick={() => setOpen(false)}
          >
            <div
              className="relative flex flex-col items-center gap-4 max-w-[90vw] max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute -top-2 -right-2 z-10 size-8 bg-black/60 hover:bg-black/80 text-white border border-white/10 rounded-full"
                >
                  <X className="size-4" />
                </Button>
              </DialogClose>

              <img
                src={url}
                alt={fileName}
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-premium-pop"
              />

              <div className="flex items-center justify-between gap-4 w-full bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2.5 border border-white/10">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {fileName}
                  </p>
                  {width && height && (
                    <p className="text-xs text-white/50">
                      {width}×{height}px
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0 text-white hover:text-white hover:bg-white/10"
                  onClick={handleDownload}
                >
                  <Download className="size-4" />
                  Download
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </>
  );
}

export default ImageAssetItem;
